import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    FEATURE_FLIGHT_CONFORMANCE,
    FEATURE_OUTSTANDING_TASKS_VIEW,
    FlightLogCriteria,
    FlightLogsService,
    PersonsOrganisationDto,
    PreferencesService,
    hasFeatureFlag
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig
} from '@flyfreely-portal-ui/flyfreely-table';
import { FormatDatePipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import { FlightLogsDialoguesService } from 'libs/flight-logs/src/lib/flight-logs-dialogues.service';
import { applyPreferences } from 'libs/flyfreely-table/src/lib/helpers';
import { EMPTY, Subject, catchError, combineLatest, finalize } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    OutstandingTasksViewTableSetupUserPreferences,
    WidgetRegistrationWithSection
} from '../../../outstanding-tasks-view.service';
import {
    OutstandingFlightLogsDataService,
    OutstandingFlightLogsFlight
} from './outstanding-flight-logs-data.service';

const RECONCILE_FLIGHT_LOGS_IDENTIFIER = 'reconcileFlightLogsTaskWidget';

@Component({
    selector: 'outstanding-flight-logs-widget',
    templateUrl: './outstanding-flight-logs.component.html',
    animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()],
    providers: [OutstandingFlightLogsDataService]
})
export class OutstandingFlightLogsWidgetComponent implements OnInit, OnDestroy {
    organisation: PersonsOrganisationDto;
    showBody = true;

    flightLogs: OutstandingFlightLogsFlight[];
    userPreferences: OutstandingTasksViewTableSetupUserPreferences;

    //Static table variables
    availableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig;
    gqlSorting: ColumnSortPreferences;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private outstandingFlightLogsDataService: OutstandingFlightLogsDataService,
        private workspaceStateService: WorkspaceStateService,
        private flightLogsService: FlightLogsService,
        private flightLogDialoguesService: FlightLogsDialoguesService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private formatDatePipe: FormatDatePipe,
        private preferencesService: PreferencesService
    ) {
        this.availableColumns = [
            {
                value: 'startTime',
                name: 'Date',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: p => this.formatDatePipe.transform(p),
                linkFunction: row => this.viewFlightLogs(row)
            },
            {
                value: 'flightLogName',
                name: 'Flight Log',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                linkFunction: row => this.viewFlightLogs(row)
            },
            {
                value: 'rpaName',
                name: 'RPA',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'rpaSerialNumber',
                name: 'RPA Serial Number',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'missionName',
                name: 'Mission',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'pilotName',
                name: 'Pilot',
                searchable: false,
                selectable: false,
                sortable: false,
                defaultSelection: true
            },
            {
                value: 'conformanceResultStatus',
                name: 'Conformance Breach',
                searchable: false,
                selectable: false,
                defaultSelection: true
            }
        ];

        this.selectedColumns = null;

        this.tableConfig = {
            serverPagination: true,
            currentPage: 0,
            limit: 3,
            actions: [],
            limitSelection: [3, 10, 25, 50, 100]
        };

        this.gqlSorting = null;

        this.outstandingFlightLogsDataService.working$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.loadPreferences();
            });

        combineLatest([
            this.outstandingFlightLogsDataService.flightLogs$,
            this.outstandingFlightLogsDataService.currentPage$,
            this.outstandingFlightLogsDataService.totalItems$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([logs, currentPage, totalItems]) => {
                this.flightLogs = logs;
                if (this.tableConfig != null) {
                    const currentConfig = this.tableConfig;
                    this.tableConfig = {
                        ...currentConfig,
                        currentPage: currentPage,
                        totalItems: totalItems
                    };
                }
            });

        this.flightLogsService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshFlightLogs());
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onToggleCollapse() {
        this.showBody = !this.showBody;
        this.userPreferences = {
            ...this.userPreferences,
            showBody: this.showBody
        };
        this.updateUserPreferences();
    }

    private loadPreferences() {
        if (!this.organisation) {
            return;
        }
        this.preferencesService
            .findPreferences<OutstandingTasksViewTableSetupUserPreferences>(
                'outstandingFlightLogsWidgetTable',
                this.organisation.id
            )
            .pipe(
                catchError(() => {
                    this.handleDefaultPreferences();
                    return EMPTY;
                }),
                finalize(() => {
                    this.refreshFlightLogs();
                })
            )
            .subscribe(preferences => {
                if (preferences === null) {
                    this.handleDefaultPreferences();
                } else {
                    this.applyPreferences(preferences);
                }
            });
    }

    private handleDefaultPreferences() {
        this.tableConfig.limit = 3; // reset limit to default
        this.applyPreferences({});
    }

    private applyPreferences(
        preferences: OutstandingTasksViewTableSetupUserPreferences
    ) {
        this.userPreferences = applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );
        this.selectedColumns = this.userPreferences.selectedColumns;
        this.gqlSorting = this.userPreferences.columnSorting;
        this.tableConfig.limit = this.userPreferences.itemLimit;
        this.showBody = this.userPreferences.showBody ?? true;
    }

    updateUserPreferences() {
        this.preferencesService
            .updatePreferences(
                'outstandingFlightLogsWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }

    refreshFlightLogs() {
        this.outstandingFlightLogsDataService.findFlightLogs(
            this.tableConfig.currentPage,
            this.tableConfig.limit,
            this.gqlSorting,
            {
                organisationId: this.organisation.id,
                assignmentStatus: FlightLogCriteria.AssignmentStatus.UNASSIGNED,
                flightLogCollectionSourceIdList: null,
                uploadedByIdList: null
            },
            this.organisation.id
        );
    }

    viewFlightLogs(flightLog: OutstandingFlightLogsFlight) {
        this.flightLogDialoguesService.showFlightLog(
            flightLog.id,
            this.organisation.id
        );
    }

    goToFlightLogs() {
        this.router.navigate(['flightlogs'], {
            queryParamsHandling: 'preserve',
            relativeTo: this.activatedRoute
        });
    }

    onTablePageChanged(page: number) {
        this.tableConfig.currentPage = page;
        this.refreshFlightLogs();
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.gqlSorting != null &&
            !this.selectedColumns.includes(this.gqlSorting.column)
        ) {
            this.gqlSorting = null;
        }
        this.refreshFlightLogs();
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.gqlSorting || null
        };
        this.updateUserPreferences();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.gqlSorting =
            sorting == null
                ? null
                : {
                      column: sorting.column.toString(),
                      ascending: sorting.ascending
                  };
        this.tableConfig.currentPage = 0;
        this.refreshFlightLogs();
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.refreshFlightLogs();
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }
}

export const reconcileFlightLogsTaskWidgetRegistration: WidgetRegistrationWithSection =
    {
        widgetIdentifier: RECONCILE_FLIGHT_LOGS_IDENTIFIER,
        section: 'operations',
        component: OutstandingFlightLogsWidgetComponent,
        isAvailable: organisation =>
            hasFeatureFlag(organisation, FEATURE_OUTSTANDING_TASKS_VIEW) &&
            hasFeatureFlag(organisation, FEATURE_FLIGHT_CONFORMANCE)
    };
